/**
 * CodingHealthBar — Tier 1 operator-facing health signals.
 *
 * Sits next to RecorderStatusPill and surfaces:
 *   • network connectivity (navigator.onLine + recent fetch failures)
 *   • camera track active / muted
 *   • mic track active / muted
 *
 * Cheap to render — no API calls, no timers beyond a 1Hz refresh tick
 * for the track-state read (MediaStreamTrack.muted can flip without
 * firing an event in some browsers).
 *
 * Goal: an operator should be able to glance at the BBP header during
 * coding and confirm "everything's fine" in <1 sec, without opening
 * the diagnostic page. Anything red → click the Health link (Tier 2).
 */
import { useEffect, useRef, useState } from "react";
import { Wifi, WifiOff, Mic, MicOff, Video, VideoOff } from "lucide-react";
import { useToast } from "../common/ToastHost";


function useTick(ms) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return tick;
}


function useOnline() {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}


export default function CodingHealthBar({ recorder, stream }) {
  // 1Hz tick — needed because MediaStreamTrack.muted state can change
  // without firing 'mute' events in some browsers (laptop camera
  // privacy shutter, OS-level muting), so we re-read on a schedule.
  useTick(1000);
  const online = useOnline();
  const toast = useToast();
  // Track previous state so we can fire a toast on the transition from
  // OK → not-OK while recording — otherwise the red dot stays silent
  // and operators miss it for minutes.
  const prevRef = useRef({ video: true, audio: true });

  // Reading directly from the stream is fine even when stream === null
  // (operator hasn't given camera permission yet). All branches fall
  // through to "no signal" — never throws.
  const videoTrack = stream?.getVideoTracks?.()?.[0] || null;
  const audioTrack = stream?.getAudioTracks?.()?.[0] || null;
  const videoActive =
    !!videoTrack && videoTrack.readyState === "live" && !videoTrack.muted;
  const audioActive =
    !!audioTrack && audioTrack.readyState === "live" && !audioTrack.muted;

  // If recording was never started, hide camera/mic indicators —
  // they'd be permanently red and just be noise. Only show during /
  // after the operator has clicked Start Recording.
  const showAvSignals = !!stream || recorder?.state === "recording";

  // Toast on drop while recording (and only while recording — outside
  // of an active session red dots are noise).
  useEffect(() => {
    if (recorder?.state !== "recording") {
      prevRef.current = { video: true, audio: true };
      return;
    }
    if (prevRef.current.video && !videoActive) {
      toast.error("Camera signal lost mid-recording — check connection / privacy shutter.");
    }
    if (prevRef.current.audio && !audioActive) {
      toast.error("Microphone signal lost mid-recording — check connection / OS mute.");
    }
    prevRef.current = { video: videoActive, audio: audioActive };
  }, [videoActive, audioActive, recorder?.state, toast]);

  const [popoverDot, setPopoverDot] = useState(null);

  // Red dots used to be silent — operators saw the colour and didn't
  // know what to do. Clicking now opens a popover with a one-line
  // diagnosis + a refresh / settings shortcut. Green dots show a short
  // "OK" tooltip on hover, no popover.
  const dotHint = (kind) => {
    if (kind === "Net") {
      return online
        ? "Browser reports connectivity. Saves are reaching the server."
        : "Browser reports offline. Saved balls will queue locally and re-sync when you're back online.";
    }
    if (kind === "Cam") {
      return videoActive
        ? "Camera track is live."
        : "No live video track. Check the camera is plugged in, the OS shutter is open, and the browser still has permission. Click anywhere else to dismiss.";
    }
    return audioActive
      ? "Microphone track is live."
      : "No live audio track. Check the mic is plugged in, the OS isn't muting, and the browser still has permission.";
  };

  const Dot = ({ ok, OkIcon, FailIcon, label }) => {
    const isOpen = popoverDot === label;
    return (
      <span className="relative inline-block">
        <button
          type="button"
          onClick={() => setPopoverDot(isOpen ? null : label)}
          className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded cursor-pointer ${
            ok
              ? "text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20"
              : "text-rose-300 bg-rose-500/10 border border-rose-500/30 hover:bg-rose-500/20"
          }`}
          title={label + (ok ? " — OK (click for details)" : " — needs attention (click for help)")}
        >
          {ok ? <OkIcon className="w-3 h-3" /> : <FailIcon className="w-3 h-3" />}
          <span className="uppercase tracking-wider">{label}</span>
        </button>
        {isOpen && (
          <div
            className="absolute top-full mt-1 left-0 z-50 w-64 text-[11px] rounded-md bg-bg-primary border border-border-default shadow-xl p-3 text-text-primary leading-snug"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`font-semibold mb-1 ${ok ? "text-emerald-300" : "text-rose-300"}`}>
              {label} · {ok ? "OK" : "Needs attention"}
            </div>
            <p className="text-text-secondary mb-2">{dotHint(label)}</p>
            <div className="flex gap-1.5">
              {!ok && (
                <button
                  onClick={() => window.location.reload()}
                  className="text-[10px] px-2 py-0.5 rounded bg-bg-secondary border border-border-default hover:bg-bg-main text-text-primary"
                >
                  Reload page
                </button>
              )}
              <button
                onClick={() => setPopoverDot(null)}
                className="text-[10px] px-2 py-0.5 rounded bg-bg-secondary border border-border-default hover:bg-bg-main text-text-secondary"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </span>
    );
  };

  // Close on outside click.
  useEffect(() => {
    if (!popoverDot) return undefined;
    const onClick = () => setPopoverDot(null);
    // Defer so the click that opened the popover doesn't immediately close it.
    const t = setTimeout(() => window.addEventListener("click", onClick), 0);
    return () => {
      clearTimeout(t);
      window.removeEventListener("click", onClick);
    };
  }, [popoverDot]);

  return (
    <div className="flex items-center gap-1.5">
      {/* 2026-05-29 — Net dot hidden per founder ask. The offline state
          is still detected internally (sync gets queued + replayed) and
          surfaces via toasts when something actually fails. The
          green-when-fine pill was just visual noise. Cam + Mic dots
          stay because they signal an active recording's health. */}
      {!online && <Dot ok={online} OkIcon={Wifi} FailIcon={WifiOff} label="Net" />}
      {showAvSignals && (
        <>
          <Dot ok={videoActive} OkIcon={Video} FailIcon={VideoOff} label="Cam" />
          <Dot ok={audioActive} OkIcon={Mic} FailIcon={MicOff} label="Mic" />
        </>
      )}
    </div>
  );
}
